<template>
  <v-card :disabled="isProcessing" :loading="isProcessing">
    <template v-slot:progress>
      <v-progress-linear absolute color="success" height="4" indeterminate></v-progress-linear>
    </template>
    <v-tabs v-model="tab" class="stock-color" left dark icons-and-text>
      <v-tabs-slider></v-tabs-slider>
      <v-tab href="#tab-1">
        <v-toolbar-title>{{ $t("STOCK.AVERAGE_COSTS.TITLE") }}</v-toolbar-title>
      </v-tab>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item value="tab-1">
        <v-card flat>
          <StockAsOfDateBar
            v-if="userStatePrepared"
            :value="asOfDate"
            :hint="$t('STOCK.AVERAGE_COSTS.AS_OF_DATE_HINT')"
            @input="onAsOfDateChange"
          />
          <v-card-text class="pa-0">
            <v-alert
              v-if="!hasSingleWarehouse"
              dense
              outlined
              type="warning"
              class="ma-3 mb-0"
            >
              {{ $t("STOCK.AVERAGE_COSTS.SINGLE_WAREHOUSE_REQUIRED") }}
            </v-alert>
            <template v-else>
              <v-row v-if="summary" dense class="px-3 pt-3">
                <v-col cols="6" sm="3">
                  <div class="caption grey--text">{{ $t("STOCK.AVERAGE_COSTS.ON_HAND_TOTAL") }}</div>
                  <div class="subtitle-2">{{ formatQty(summary.quantity_on_hand) }}</div>
                </v-col>
                <v-col cols="6" sm="3">
                  <div class="caption grey--text">{{ $t("STOCK.AVERAGE_COSTS.WEIGHTED_AVG_COST") }}</div>
                  <div class="subtitle-2">
                    {{ formatMoney(summary.weighted_average_cost, summary.currency_code) }}
                  </div>
                </v-col>
                <v-col cols="6" sm="3">
                  <div class="caption grey--text">{{ $t("STOCK.AVERAGE_COSTS.TOTAL_INVENTORY_VALUE") }}</div>
                  <div class="subtitle-2">
                    {{ formatMoney(summary.total_inventory_value, summary.currency_code) }}
                  </div>
                </v-col>
              </v-row>
              <v-alert
                v-if="summary && Number(summary.quantity_on_hand) > 0 && summary.weighted_average_cost == null"
                dense
                outlined
                type="info"
                class="mx-3 mb-0"
              >
                {{ $t("STOCK.INVENTORY.NO_AVG_COST_HINT") }}
              </v-alert>
              <v-alert
                v-if="canRevalue && !isViewingToday"
                dense
                outlined
                type="info"
                class="mx-3 mb-0"
              >
                {{ $t("STOCK.AVERAGE_COSTS.REVALUE_TODAY_ONLY_HINT") }}
              </v-alert>
              <Table
                v-if="userStatePrepared"
                ref="stock-average-costs-table"
                height="500"
                name="stock-average-costs-table"
                :headers="headers"
                :headers-slots="['quantity_breakdown']"
                :server-data-options="{ method: tableItemsMethod }"
                :items-per-page="100"
                :loading="isProcessing"
                :filters="tableFilters"
                dense
                fixed-header
                filterable
                @filters-change="onTableFiltersChange"
                @search="onTableSearchChange"
                @fetched-server-data="loadSummary"
              >
                <template v-slot:header.quantity_breakdown>
                  <div class="text-left">
                    <div>{{ $t("STOCK.PRODUCTS.PACKAGING") }}</div>
                    <div class="caption grey--text font-weight-regular">
                      {{ $t("STOCK.AVERAGE_COSTS.PACKAGING_COST_SUBTITLE") }}
                    </div>
                  </div>
                </template>
                <template
                  v-for="(header, index) in headers"
                  v-slot:[`item.${header.value}`]="{ item }"
                >
                  <template v-if="header.value === 'product.display_name'">
                    <span :key="index">{{ item.item.product ? item.item.product.display_name : "-" }}</span>
                  </template>
                  <template v-else-if="header.value === 'product.code'">
                    <span :key="index">{{ item.item.product ? item.item.product.code : "-" }}</span>
                  </template>
                  <template v-else-if="header.value === 'product.stock_product_department.label'">
                    <span :key="index">
                      {{
                        item.item.product && item.item.product.stock_product_department
                          ? item.item.product.stock_product_department.label
                          : "-"
                      }}
                    </span>
                  </template>
                  <template v-else-if="header.value === 'product.stock_product_category.label'">
                    <span :key="index">
                      {{
                        item.item.product && item.item.product.stock_product_category
                          ? item.item.product.stock_product_category.label
                          : "-"
                      }}
                    </span>
                  </template>
                  <template v-else-if="header.value === 'product.temperature.label'">
                    <span :key="index">
                      {{
                        item.item.product && item.item.product.temperature
                          ? item.item.product.temperature.label
                          : "-"
                      }}
                    </span>
                  </template>
                  <template v-else-if="header.value === 'quantity_breakdown'">
                    <StockPackagingBreakdownCell
                      :key="index"
                      :breakdown="item.item.quantity_breakdown"
                      show-cost
                      :average-cost="item.item.average_cost"
                      :currency-code="item.item.currency_code"
                    />
                  </template>
                  <template v-else-if="header.value === 'product.stock_tags'">
                    <template
                      v-if="item.item.product && item.item.product.stock_tags && item.item.product.stock_tags.length"
                    >
                      <v-chip
                        v-for="tag in item.item.product.stock_tags"
                        :key="tag.id"
                        x-small
                        class="ma-1"
                        color="teal"
                        dark
                      >
                        {{ tag.label }}
                      </v-chip>
                    </template>
                    <span v-else :key="index">-</span>
                  </template>
                  <template v-else-if="header.value === 'average_cost'">
                    {{ formatMoney(item.item.average_cost, item.item.currency_code) }}
                  </template>
                  <template v-else-if="header.value === 'inventory_value'">
                    {{ formatMoney(item.item.inventory_value, item.item.currency_code) }}
                  </template>
                  <template v-else-if="header.value === 'quantity_on_hand'">
                    {{ formatQty(item.item.quantity_on_hand, item.item.product) }}
                  </template>
                  <span
                    v-else-if="header.value === 'options'"
                    :key="index"
                    class="d-flex align-center justify-center flex-nowrap"
                  >
                    <v-btn
                      v-if="canRevalueCurrent && Number(item.item.quantity_on_hand) > 0"
                      icon
                      color="primary"
                      :title="$t('STOCK.AVERAGE_COSTS.REVALUE')"
                      @click="openRevalueDialog(item.item)"
                    >
                      <v-icon dark>mdi-currency-usd</v-icon>
                    </v-btn>
                  </span>
                  <template v-else>
                    {{ item.value ?? "-" }}
                  </template>
                </template>
              </Table>
            </template>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <v-dialog v-model="showRevalueDialog" max-width="480" persistent>
      <v-card>
        <v-card-title>{{ $t("STOCK.AVERAGE_COSTS.REVALUE_TITLE") }}</v-card-title>
        <v-card-text>
          <p class="body-2 grey--text text--darken-1 mb-4">
            {{ $t("STOCK.AVERAGE_COSTS.REVALUE_HINT") }}
          </p>
          <div v-if="revalueTarget" class="mb-3">
            <div class="subtitle-2">
              {{ revalueTarget.product ? revalueTarget.product.display_name : "-" }}
            </div>
            <div class="caption grey--text">
              {{ $t("STOCK.INVENTORY.ON_HAND") }}:
              {{ formatQty(revalueTarget.quantity_on_hand, revalueTarget.product) }}
            </div>
            <div class="caption grey--text">
              {{ $t("STOCK.AVERAGE_COSTS.CURRENT_AVG_COST") }}:
              {{ formatMoney(revalueTarget.average_cost, revalueTarget.currency_code) }}
            </div>
          </div>
          <v-text-field
            v-model="revalueForm.new_avg_cost"
            type="number"
            step="0.0001"
            min="0"
            :label="$t('STOCK.AVERAGE_COSTS.NEW_AVG_COST') + '*'"
            :error-messages="revalueErrors.new_avg_cost || []"
            dense
            outlined
            hide-details="auto"
            class="mb-3"
          />
          <v-textarea
            v-model="revalueForm.reason"
            :label="$t('STOCK.AVERAGE_COSTS.REASON') + '*'"
            :error-messages="revalueErrors.reason || []"
            rows="3"
            dense
            outlined
            hide-details="auto"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="isProcessing" @click="closeRevalueDialog">
            {{ $t("GENERAL.LABELS.CANCEL") }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="isProcessing"
            :disabled="!canSubmitRevalue"
            @click="submitRevalue"
          >
            {{ $t("STOCK.AVERAGE_COSTS.REVALUE") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import Table from "@/views/partials/v-table/Table.vue";
import StockAsOfDateBar from "@/views/partials/stock/StockAsOfDateBar.vue";
import StockPackagingBreakdownCell from "@/views/partials/stock/StockPackagingBreakdownCell.vue";
import stockAsOfDateTableMixin from "@/views/partials/stock/stockAsOfDateTable.mixin";
import { SHOW_SNACK } from "@/store/snack.module";
import {
  GET_STOCK_AVERAGE_COSTS,
  GET_STOCK_AVERAGE_COSTS_SUMMARY,
  REVALUE_STOCK_AVERAGE_COST,
} from "@/store/stock/stockAverageCosts.module";
import {
  GET_STOCK_TEMPERATURE_OPTIONS,
  GET_STOCK_TAG_OPTIONS,
} from "@/store/stock/stockProducts.module";
import { GET_STOCK_PRODUCT_DEPARTMENT_OPTIONS } from "@/store/stock/stockProductDepartments.module";
import { GET_STOCK_PRODUCT_CATEGORY_OPTIONS } from "@/store/stock/stockProductCategories.module";

export default {
  name: "StockAverageCosts",
  mixins: [stockAsOfDateTableMixin],
  components: { Table, StockAsOfDateBar, StockPackagingBreakdownCell },
  data() {
    return {
      stockTableRef: "stock-average-costs-table",
      tab: "tab-1",
      isProcessing: false,
      userStatePrepared: false,
      tableItemsMethod: GET_STOCK_AVERAGE_COSTS,
      tableFilters: [],
      allDepartments: [],
      allCategories: [],
      activeDepartmentIds: [],
      summary: null,
      showRevalueDialog: false,
      revalueTarget: null,
      revalueForm: {
        new_avg_cost: "",
        reason: "",
      },
      revalueErrors: {},
      headers: [
        { text: this.$t("STOCK.PRODUCTS.CODE"), value: "product.code", align: "center", width: "8%" },
        {
          text: this.$t("STOCK.PRODUCTS.DISPLAY_NAME"),
          value: "product.display_name",
          align: "center",
          width: "15%",
        },
        {
          text: this.$t("STOCK.PRODUCTS.DEPARTMENT"),
          value: "product.stock_product_department.label",
          sortable: false,
          align: "center",
          width: "8%",
        },
        {
          text: this.$t("STOCK.PRODUCTS.CATEGORY"),
          value: "product.stock_product_category.label",
          sortable: false,
          align: "center",
          width: "8%",
        },
        {
          text: this.$t("STOCK.PRODUCTS.TEMPERATURE"),
          value: "product.temperature.label",
          sortable: false,
          align: "center",
          width: "7%",
        },
        {
          text: this.$t("STOCK.PRODUCTS.TAGS"),
          value: "product.stock_tags",
          sortable: false,
          align: "center",
          width: "11%",
        },
        {
          text: this.$t("STOCK.PRODUCTS.PACKAGING"),
          value: "quantity_breakdown",
          sortable: false,
          align: "left",
          width: "22%",
        },
        {
          text: this.$t("STOCK.INVENTORY.ON_HAND"),
          value: "quantity_on_hand",
          align: "center",
          width: "10%",
        },
        {
          text: this.$t("STOCK.AVERAGE_COSTS.AVG_COST"),
          value: "average_cost",
          align: "center",
          width: "11%",
        },
        {
          text: this.$t("STOCK.AVERAGE_COSTS.INVENTORY_VALUE"),
          value: "inventory_value",
          align: "center",
          width: "11%",
        },
        {
          text: this.$t("STOCK.AVERAGE_COSTS.ACTIONS"),
          value: "options",
          sortable: false,
          align: "center",
          width: "6%",
        },
      ],
    };
  },
  computed: {
    stockContextVersion() {
      return this.$store.getters.stockContextVersion;
    },
    hasSingleWarehouse() {
      return (this.$store.getters.stockContextWarehouseIds || []).length === 1;
    },
    selectedWarehouseId() {
      const ids = this.$store.getters.stockContextWarehouseIds || [];
      return ids.length === 1 ? ids[0] : null;
    },
    canRevalue() {
      return this.hasPermissions("stock-average-costs-revalue");
    },
    isViewingToday() {
      return (this.asOfDate || this.todayIsoDate()) === this.todayIsoDate();
    },
    canRevalueCurrent() {
      return this.canRevalue && this.isViewingToday;
    },
    canSubmitRevalue() {
      const cost = this.revalueForm.new_avg_cost;
      const reason = (this.revalueForm.reason || "").trim();
      return (
        this.canRevalueCurrent &&
        cost !== "" &&
        cost !== null &&
        Number(cost) >= 0 &&
        reason.length >= 3
      );
    },
  },
  watch: {
    asOfDate() {
      if (!this.isViewingToday && this.showRevalueDialog) {
        this.closeRevalueDialog();
      }
    },
    stockContextVersion() {
      this.reloadTable();
      this.loadSummary();
    },
    userStatePrepared(ready) {
      if (ready && this.hasSingleWarehouse) {
        this.$nextTick(() => {
          this.initAsOfDateFromTable(this.stockTableRef, { useToday: true });
          this.loadSummary();
        });
      }
    },
    hasSingleWarehouse(single) {
      if (single && this.userStatePrepared) {
        this.$nextTick(() => {
          this.initAsOfDateFromTable(this.stockTableRef, { useToday: true });
          this.loadSummary();
        });
      } else {
        this.summary = null;
      }
    },
  },
  mounted() {
    this.loadFilters().finally(() => {
      this.userStatePrepared = true;
    });
  },
  methods: {
    onAsOfDateChange(value) {
      this.asOfDate = value || this.todayIsoDate();
      this.$nextTick(() => {
        this.applyAsOfDateToTable(this.stockTableRef);
        this.loadSummary();
      });
    },
    loadFilters() {
      return Promise.all([
        this.$store.dispatch(GET_STOCK_PRODUCT_DEPARTMENT_OPTIONS, {}),
        this.$store.dispatch(GET_STOCK_PRODUCT_CATEGORY_OPTIONS, {}),
        this.$store.dispatch(GET_STOCK_TEMPERATURE_OPTIONS),
        this.$store.dispatch(GET_STOCK_TAG_OPTIONS, {}),
      ])
        .then(([departmentsResponse, categoriesResponse, temperaturesResponse, tagsResponse]) => {
          this.allDepartments = departmentsResponse.data || departmentsResponse || [];
          this.allCategories = categoriesResponse.data || categoriesResponse || [];
          this.buildTableFilters(
            this.allDepartments,
            this.getFilteredCategories([]),
            temperaturesResponse.data || [],
            tagsResponse.data || []
          );
        })
        .catch(() => {
          this.tableFilters = [];
        });
    },
    getFilteredCategories(departmentIds) {
      if (!departmentIds.length) {
        return this.allCategories;
      }

      return this.allCategories.filter((category) =>
        departmentIds.includes(category.stock_product_department_id)
      );
    },
    buildTableFilters(departments, categories, temperatures, tags) {
      this.tableFilters = [
        {
          key: "stock_product_department_id",
          label: this.$t("STOCK.PRODUCTS.DEPARTMENT"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: departments,
        },
        {
          key: "stock_product_category_id",
          label: this.$t("STOCK.PRODUCTS.CATEGORY"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: categories,
        },
        {
          key: "temperature_id",
          label: this.$t("STOCK.PRODUCTS.TEMPERATURE"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: temperatures,
        },
        {
          key: "stock_tag_ids",
          label: this.$t("STOCK.PRODUCTS.TAGS"),
          type: "select",
          "item-value": "id",
          "item-text": "label",
          items: tags,
        },
      ];
    },
    pruneCategoryFilterSelection(filters, categories) {
      const selectedCategoryIds = filters.stock_product_category_id || [];
      if (!selectedCategoryIds.length) {
        return false;
      }

      const allowedIds = new Set(categories.map((category) => category.id));
      const pruned = selectedCategoryIds.filter((id) => allowedIds.has(id));
      if (JSON.stringify(pruned) === JSON.stringify(selectedCategoryIds)) {
        return false;
      }

      filters.stock_product_category_id = pruned;
      const table = this.$refs[this.stockTableRef];
      if (table) {
        table.serverFilters = {
          ...table.serverFilters,
          ...filters,
        };
      }
      return true;
    },
    onTableFiltersChange(filters = {}) {
      const departmentIds = filters.stock_product_department_id || [];
      const departmentChanged =
        JSON.stringify(departmentIds) !== JSON.stringify(this.activeDepartmentIds);

      if (departmentChanged) {
        this.activeDepartmentIds = [...departmentIds];
        const filteredCategories = this.getFilteredCategories(departmentIds);
        const pruned = this.pruneCategoryFilterSelection(filters, filteredCategories);
        this.buildTableFilters(
          this.allDepartments,
          filteredCategories,
          this.tableFilters.find((filter) => filter.key === "temperature_id")?.items || [],
          this.tableFilters.find((filter) => filter.key === "stock_tag_ids")?.items || []
        );
        if (pruned) {
          this.reloadTable();
          return;
        }
      }

      this.loadSummary();
    },
    onTableSearchChange() {
      this.loadSummary();
    },
    buildSummaryParams() {
      const table = this.$refs[this.stockTableRef];
      const serverFilters = table?.serverFilters || {};
      const search = (table?.searchString || "").trim();

      return {
        as_of_date: this.asOfDate,
        search: search.length >= 2 ? search : undefined,
        filters: serverFilters,
      };
    },
    loadSummary() {
      if (!this.hasSingleWarehouse) {
        this.summary = null;
        return;
      }
      this.$store
        .dispatch(GET_STOCK_AVERAGE_COSTS_SUMMARY, this.buildSummaryParams())
        .then((data) => {
          this.summary = data;
        })
        .catch(() => {
          this.summary = null;
        });
    },
    reloadTable() {
      if (!this.hasSingleWarehouse) {
        return;
      }
      this.$nextTick(() => {
        const table = this.$refs[this.stockTableRef];
        if (!table?.fetchServerData) {
          return;
        }
        table
          .fetchServerData()
          .catch((errors) => {
            this.$store.dispatch(SHOW_SNACK, {
              message: this.formatApiError(errors),
              color: "error",
            });
          });
      });
    },
    openRevalueDialog(row) {
      if (!this.canRevalueCurrent) {
        return;
      }
      this.revalueTarget = row;
      this.revalueForm = {
        new_avg_cost:
          row.average_cost != null && row.average_cost !== ""
            ? String(row.average_cost)
            : "",
        reason: "",
      };
      this.revalueErrors = {};
      this.showRevalueDialog = true;
    },
    closeRevalueDialog() {
      this.showRevalueDialog = false;
      this.revalueTarget = null;
      this.revalueErrors = {};
    },
    submitRevalue() {
      if (!this.revalueTarget || !this.selectedWarehouseId || !this.canSubmitRevalue) {
        return;
      }

      const productId = this.revalueTarget.product
        ? this.revalueTarget.product.id
        : null;
      if (!productId) {
        return;
      }

      this.isProcessing = true;
      this.revalueErrors = {};

      this.$store
        .dispatch(REVALUE_STOCK_AVERAGE_COST, {
          warehouse_id: this.selectedWarehouseId,
          stock_product_id: productId,
          new_avg_cost: Number(this.revalueForm.new_avg_cost),
          reason: (this.revalueForm.reason || "").trim(),
        })
        .then(() => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("STOCK.AVERAGE_COSTS.REVALUE_SUCCESS"),
            color: "success",
          });
          this.closeRevalueDialog();
          this.reloadTable();
          this.loadSummary();
        })
        .catch((errors) => {
          this.revalueErrors = errors?.errors || {};
          this.$store.dispatch(SHOW_SNACK, {
            message: this.formatApiError(errors),
            color: "error",
          });
        })
        .finally(() => {
          this.isProcessing = false;
        });
    },
    formatApiError(errors) {
      if (errors?.message) {
        return errors.message;
      }
      if (errors?.errors) {
        return Object.values(errors.errors).flat().join(" ");
      }
      return this.$t("GENERAL.UNEXPECTED_ERROR");
    },
    formatMoney(value, currency) {
      if (value == null || value === "") {
        return "-";
      }
      return `${Number(value).toFixed(4)} ${currency || ""}`.trim();
    },
    formatQty(value, product) {
      const unit =
        product && product.primary_unit ? product.primary_unit.code : "";
      const qty = Number(value || 0).toLocaleString(undefined, {
        maximumFractionDigits: 4,
      });
      return unit ? `${qty} ${unit}` : qty;
    },
  },
};
</script>
